/**
 * ModuleAccessGuard Component
 *
 * Protects pages that require at least a module permission level in the
 * current tenant (e.g. admin on the `runner` module for runner policies).
 * Shows an "Access Denied" message and redirects users without it.
 */

import { Navigate } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { useModuleAccess, type ModuleLevel } from '@/hooks/useModuleAccess';

interface ModuleAccessGuardProps {
  module: string;
  level: ModuleLevel;
  children: React.ReactNode;
  /** Where to redirect when access is denied. Defaults to '/integrations' */
  redirectTo?: string;
}

export function ModuleAccessGuard({
  module,
  level,
  children,
  redirectTo = '/integrations',
}: ModuleAccessGuardProps) {
  const hasAccess = useModuleAccess(module, level);

  if (!hasAccess) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <ShieldAlert className="h-12 w-12 text-destructive" />
        <div className="text-center">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Access Denied
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            This area requires the `{module}` module permission (at least
            `{level}`) in the current tenant.
          </p>
        </div>
        <Navigate to={redirectTo} replace />
      </div>
    );
  }

  return <>{children}</>;
}